// v-fade-in — fade an <img> in once it finishes loading.
//
// Why a directive: the shared MediaCard poster + FeaturedHero already fade, but
// a dozen other poster/hero/logo <img>s (Put.io file grid, Live TV logos,
// detail-page backdrops, Plex/Watchlist shelves) popped in hard. This applies
// the same effect everywhere with one attribute and no per-component state.
//
// Performance: it animates ONLY `opacity`, which the browser composites on the
// GPU without re-laying-out or re-painting siblings — so even a grid of posters
// fading in as they scroll into view stays cheap. We deliberately do NOT set
// `will-change` (promoting hundreds of layers would cost more than it saves),
// don't re-fade on src changes, and reveal instantly for already-cached images
// (complete + naturalWidth) so there's no pointless flash. Honors
// prefers-reduced-motion.
//
// Usage: `<img v-fade-in :src="…">` (default 300ms) or `v-fade-in="600"` for a
// slower, more cinematic backdrop fade.

const DEFAULT_MS = 300;

export const fadeIn = {
  mounted(el, binding) {
    if (el.tagName !== "IMG") return;

    const ms = Number(binding?.value) > 0 ? Number(binding.value) : DEFAULT_MS;
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const reveal = () => {
      el.style.opacity = "1";
    };

    if (reduceMotion) {
      reveal();
      return;
    }

    el.style.transition = `opacity ${ms}ms ease`;
    // Reveal on load; also on error so a broken/blocked image never leaves the
    // box permanently invisible (the alt / fallback still shows). { once }
    // auto-removes each listener after it fires (reveal is one-shot anyway).
    el.addEventListener("load", reveal, { once: true });
    el.addEventListener("error", reveal, { once: true });

    // Already decoded (HTTP/memory cache) → show immediately, no fade. An <img>
    // with no real src yet (lazy) reports complete:true but naturalWidth:0, so
    // it correctly starts hidden and fades when its src finally loads.
    if (el.complete && el.naturalWidth > 0) reveal();
    else el.style.opacity = "0";
  },
};
